import type {
  Configuration,
  SurfaceMaps,
  SurfaceTypesMapValue,
  SurfaceOptionsGroup,
} from '~/types/configurator'

export default function useConfigurationPrice(
  configuration: Ref<Configuration | null>,
  currentValues: ComputedRef<Configuration['values']>,
  surfaceTypes: ComputedRef<Map<number, SurfaceTypesMapValue>>,
  surfaceMaps: ComputedRef<SurfaceMaps>,
  surfaceOptionsGroups: ComputedRef<Map<string, SurfaceOptionsGroup>>,
  surfaceCategoryMismatch: ComputedRef<Map<string, Map<number, boolean>>>
) {
  const mostExpensiveSelected = computed(() => {
    let mostExpensiveTypeNid: number | undefined
    let mostExpensiveCategory:
      | {
          nid: number
          priceDifference: number | undefined
        }
      | undefined

    if (configuration.value) {
      ;[...surfaceOptionsGroups.value.values()].forEach(
        (surfaceOptionsGroup) => {
          ;[...surfaceOptionsGroup.surfaceOptionGroups.values()].forEach(
            (optionGroup) => {
              const surfaceOptions = optionGroup.surfaceOptions.filter(
                (option) => option.hasImpactOnPrice
              )
              surfaceOptions.forEach((option) => {
                const { partCategoryNid, accessoryNid } = option
                const surfaceNid = configuratorUtils.findValue(
                  currentValues.value,
                  partCategoryNid,
                  accessoryNid
                )?.surfaceNid
                const { type, category } = configuratorUtils.findSurfaceByNid(
                  surfaceTypes.value,
                  surfaceMaps.value,
                  surfaceNid
                )
                if (
                  type &&
                  category &&
                  category.priceDifference &&
                  (!mostExpensiveCategory ||
                    (mostExpensiveCategory.priceDifference &&
                      mostExpensiveCategory.priceDifference <
                        category.priceDifference))
                ) {
                  mostExpensiveCategory = {
                    nid: category.nid,
                    priceDifference: category.priceDifference,
                  }
                  mostExpensiveTypeNid = type.nid
                }
              })
            }
          )
        }
      )
    }

    return {
      typeNid: mostExpensiveTypeNid,
      category: mostExpensiveCategory,
    }
  })

  const surfaceOptionsGroupsPrices = computed(() => {
    let usedMostExpensiveCategory: boolean = false
    return [...surfaceOptionsGroups.value.entries()].reduce(
      (acc, [key, surfaceOptionsGroup]) => {
        // surfaceOptionGroupPrice = category price (if it's priciest and it's applied on base part that has impact on price) + collections prices + surfaces prices

        const surfaceOptionGroupsPriceMap = [
          ...surfaceOptionsGroup.surfaceOptionGroups.entries(),
        ].reduce((acc, [nid, surfaceOptionGroup]) => {
          if (surfaceCategoryMismatch.value.get(key)?.get(nid)) {
            // if mismatch skip
            return acc
          }
          const { surfaceOptions } = surfaceOptionGroup
          let surfaceOptionGroupPrice = 0
          const usedCategories = new Set<number>()
          surfaceOptions.forEach((option) => {
            const { partCategoryNid, accessoryNid } = option
            const surfaceNid = configuratorUtils.findValue(
              currentValues.value,
              partCategoryNid,
              accessoryNid
            )?.surfaceNid
            const { category } = configuratorUtils.findSurfaceByNid(
              surfaceTypes.value,
              surfaceMaps.value,
              surfaceNid
            )

            if (category && !usedCategories.has(category.nid)) {
              // add category price difference once (if it's priciest and it's applied on base part that has impact on price)
              if (
                category?.nid &&
                mostExpensiveSelected.value.category?.nid &&
                category.nid === mostExpensiveSelected.value.category.nid &&
                option.hasImpactOnPrice &&
                !usedMostExpensiveCategory
              ) {
                surfaceOptionGroupPrice += category?.priceDifference || 0
                usedMostExpensiveCategory = true
              }

              const surfaceNids = [
                ...(category?.surfaceCollections.values() || []),
              ]
                .map((collection) =>
                  [...(collection.surfaces.values() || [])].map(
                    (surface) => surface.nid
                  )
                )
                .flat()

              // get values with surface from category that are linked to given surfaceOptions
              const valuesWithSurfaceForCategory = currentValues.value
                .filter(
                  (value) =>
                    value.surfaceNid !== null &&
                    value.surfaceNid !== undefined &&
                    surfaceNids?.includes(value.surfaceNid)
                )
                .filter((value) =>
                  surfaceOptions.find(
                    (option) => option.partCategoryNid === value.partCategoryNid
                  )
                )

              for (const value of valuesWithSurfaceForCategory) {
                const { collection, surface } =
                  configuratorUtils.findSurfaceByNid(
                    surfaceTypes.value,
                    surfaceMaps.value,
                    value.surfaceNid
                  )
                // add mesh price
                collection?.priceIncrements.forEach((priceIncrement) => {
                  if (
                    priceIncrement.accessoryCategoryNid &&
                    priceIncrement.accessoryNid &&
                    configuration.value
                  ) {
                    surfaceOptionGroupPrice +=
                      configuratorUtils.findAccessory(
                        configuration.value.accessoriesOptions,
                        priceIncrement.accessoryCategoryNid,
                        priceIncrement.accessoryNid
                      ).accessory?.price || 0
                  }
                })
                // add wood staining price
                surface?.priceIncrements.forEach((priceIncrement) => {
                  if (
                    priceIncrement.accessoryCategoryNid &&
                    priceIncrement.accessoryNid &&
                    configuration.value
                  ) {
                    surfaceOptionGroupPrice +=
                      configuratorUtils.findAccessory(
                        configuration.value.accessoriesOptions,
                        priceIncrement.accessoryCategoryNid,
                        priceIncrement.accessoryNid
                      ).accessory?.price || 0
                  }
                })
              }
              usedCategories.add(category.nid)
            }
          })

          return acc.set(nid, surfaceOptionGroupPrice)
        }, new Map<number, number>())

        acc.set(key, surfaceOptionGroupsPriceMap)
        return acc
      },
      new Map<string, Map<number, number>>()
    )
  })

  const price = computed(() => {
    const basePrice = configuration.value?.price || 0
    const accessoriesPrice =
      currentValues.value.reduce((acc, value) => {
        const { accessory } = configuratorUtils.findAccessory(
          configuration.value?.accessoriesOptions,
          value.partCategoryNid,
          value.partNid
        )
        if (accessory?.isRemovable) {
          return acc + (accessory.price || 0)
        }
        return acc
      }, 0) || 0
    const surfacePrice = [...surfaceOptionsGroupsPrices.value.values()].reduce(
      (acc, surfaceOptionsGroupsPrice) => {
        return (
          acc +
          [...surfaceOptionsGroupsPrice.values()].reduce(
            (acc, price) => acc + price,
            0
          )
        )
      },
      0
    )

    return basePrice + accessoriesPrice + surfacePrice
  })

  return { surfaceOptionsGroupsPrices, price }
}
